import { Dropdown } from '@design/components/dropdown/Dropdown';
import clsx from 'clsx';
import {
  FocusEvent,
  HTMLAttributes,
  KeyboardEvent,
  Ref,
  RefObject,
  Suspense,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react';
import { setCaretToEnd } from './clipboard';
import styles from './MentionComposerInput.module.scss';
import {
  isMentionSectionTitleRow,
  MentionDropdownItem,
  MentionPopupRow,
} from './MentionPopup';
import { SerializedEditorContent } from './serialize';
import { findActiveTrigger, trimMentionQuery } from './trigger-detection';
import { wrapUnresolvedToken } from './unresolved-mention';
import { useMentionClipboard } from './useMentionClipboard';
import { useMentionEditor } from './useMentionEditor';
import { useMentionPopup } from './useMentionPopup';
import { useResolvedMentionPreview } from './useResolvedMentionPreview';
import { useUnresolvedMentions } from './useUnresolvedMentions';

export interface MentionComposerInputHandle {
  focus: () => void;
  clear: () => void;
  setText: (text: string) => void;
}

export interface MentionComposerInputProps {
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  className?: string;
  onChange?: (content: SerializedEditorContent) => void;
  onSubmit?: () => void;
  onFocus?: () => void;
  ref?: Ref<MentionComposerInputHandle>;
  'data-testid'?: string;
}

interface MentionEditableRootProps extends HTMLAttributes<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>;
  editorRef: RefObject<HTMLDivElement | null>;
}

// Dropdown clones children and replaces `ref`; merge so we keep `editorRef`.
const MentionEditableRoot = ({
  ref,
  editorRef,
  ...rest
}: MentionEditableRootProps) => (
  <div
    ref={node => {
      editorRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    }}
    {...rest}
  />
);

export const MentionComposerInput = ({
  placeholder,
  autoFocus,
  disabled,
  className,
  onChange,
  onSubmit,
  onFocus,
  ref,
  'data-testid': dataTestId,
}: MentionComposerInputProps) => {
  const refreshTriggerRef = useRef<(root: HTMLElement) => void>(() => {});
  const closeUnresolvedTriggerRef = useRef<(root: HTMLElement) => void>(
    () => {}
  );

  const editor = useMentionEditor({
    autoFocus,
    onChange,
    onSubmit,
    onAfterRecompute: root => refreshTriggerRef.current(root),
    onBeforeCaretRestore: root => closeUnresolvedTriggerRef.current(root),
  });

  const popup = useMentionPopup({
    editorRef: editor.editorRef,
    emitChange: editor.emitChange,
  });
  refreshTriggerRef.current = popup.refreshTriggerFromEditor;

  closeUnresolvedTriggerRef.current = root => {
    const trigger = popup.activeTrigger;
    if (!trigger || trigger.startNode == null || trigger.startOffset == null) {
      return;
    }
    const query = trimMentionQuery(trigger.query);
    if (findActiveTrigger(root) || !popup.hadMatchForActiveTrigger || !query) {
      return;
    }
    wrapUnresolvedToken({
      startNode: trigger.startNode,
      startOffset: trigger.startOffset,
      trigger: trigger.trigger,
      query,
    });
  };

  // No match at all: leave the token as plain text (just continue typing).
  // Partial match (had candidates but nothing selected): highlight it as
  // unresolved so the user can revisit it later.
  const closeActiveTrigger = () => {
    const trigger = popup.activeTrigger;
    const query = trigger ? trimMentionQuery(trigger.query) : '';
    if (
      trigger &&
      trigger.startNode != null &&
      trigger.startOffset != null &&
      popup.hadMatchForActiveTrigger &&
      query
    ) {
      wrapUnresolvedToken({
        startNode: trigger.startNode,
        startOffset: trigger.startOffset,
        trigger: trigger.trigger,
        query,
      });
      editor.emitChange();
    }
    popup.setActiveTrigger(null);
  };

  const unresolved = useUnresolvedMentions({
    editorRef: editor.editorRef,
    applyTrigger: popup.applyTrigger,
  });

  const chipPreview = useResolvedMentionPreview({
    editorRef: editor.editorRef,
  });

  const clipboard = useMentionClipboard({
    editorRef: editor.editorRef,
    disabled,
    recompute: editor.recompute,
    clearChipSelection: editor.clearChipSelection,
  });

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (popup.activeTrigger && popup.handlePopupKeyDown(e)) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (editor.handleEnterKey(e)) {
      e.preventDefault();
      return;
    }

    if (e.key === 'Backspace' && editor.handleChipBackspace()) {
      e.preventDefault();
      return;
    }

    if (!e.metaKey && !e.ctrlKey) {
      editor.clearChipSelection();
    }
  };

  const onKeyUp = (e: KeyboardEvent<HTMLDivElement>) => {
    if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
      const root = editor.editorRef.current;
      if (root) {
        popup.refreshTriggerFromEditor(root);
      }
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        const root = editor.editorRef.current;
        if (!root) {
          return;
        }
        const alreadyFocused = document.activeElement === root;
        root.focus();
        if (!alreadyFocused) {
          setCaretToEnd(root);
        }
      },
      clear: () => {
        editor.clearEditorDom();
        popup.setActiveTrigger(null);
        unresolved.clearHoveredUnresolved();
        chipPreview.clearHoveredChip();
        popup.clearSources();
      },
      setText: (text: string) => {
        editor.setEditorText(text);
        popup.setActiveTrigger(null);
        unresolved.clearHoveredUnresolved();
        chipPreview.clearHoveredChip();
      },
    }),
    [editor, popup, unresolved, chipPreview]
  );

  const openUnresolvedOrRecompute = useCallback(
    (e: Parameters<typeof unresolved.openUnresolvedFromEvent>[0]) => {
      if (!unresolved.openUnresolvedFromEvent(e)) {
        editor.recompute();
      }
    },
    [unresolved, editor]
  );

  const onBlur = (e: FocusEvent<HTMLDivElement>) => {
    const next = e.relatedTarget;
    if (
      next instanceof Node &&
      (e.currentTarget.contains(next) ||
        (next instanceof Element && next.closest('[data-dropdown-list]')))
    ) {
      return;
    }
    closeActiveTrigger();
  };

  return (
    <div
      className={clsx(styles.wrapper, styles.withPlaceholderGrid, className)}
    >
      <Dropdown<MentionDropdownItem>
        enableFloating
        strategy="fixed"
        placement="bottom-start"
        offsetValue={{ y: 8 }}
        inPortal
        shouldShowItems={!!popup.activeTrigger}
        shouldCloseOnOutsideClick
        onOutsideClick={closeActiveTrigger}
        dropdownItems={popup.dropdownItems}
        renderDropdownItem={(item, _isActive, isFocused) => (
          <div
            className={styles.rowPressTarget}
            onMouseDown={event => {
              event.preventDefault();
              if (!isMentionSectionTitleRow(item)) {
                popup.insertCandidate(item);
              }
            }}
            onClick={event => {
              event.stopPropagation();
              if (!isMentionSectionTitleRow(item)) {
                popup.insertCandidate(item);
              }
            }}
          >
            <Suspense fallback={null}>
              <MentionPopupRow
                item={item}
                isFocused={isFocused}
                searchQuery={popup.activeTrigger?.query ?? ''}
              />
            </Suspense>
          </div>
        )}
        selectItem={item => {
          if (!isMentionSectionTitleRow(item)) {
            popup.insertCandidate(item);
          }
        }}
        dropdownApiRef={popup.dropdownApiRef}
        focusedItemIdx={popup.dropdownItems.findIndex(
          item => !isMentionSectionTitleRow(item)
        )}
        className={styles.popupList}
        data-testid="mention-popup"
      >
        <MentionEditableRoot
          editorRef={editor.editorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          spellCheck={false}
          className={styles.editor}
          data-disabled={disabled}
          data-testid={dataTestId ?? 'mention-composer-input'}
          data-chat-textarea-element
          onInput={editor.recompute}
          onMouseUp={openUnresolvedOrRecompute}
          onMouseOver={e => {
            unresolved.onUnresolvedMouseOver(e);
            chipPreview.onChipMouseOver(e);
          }}
          onMouseOut={e => {
            unresolved.onUnresolvedMouseOut(e);
            chipPreview.onChipMouseOut(e);
          }}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          onCopy={clipboard.onCopy}
          onCut={clipboard.onCut}
          onPaste={clipboard.onPaste}
          onFocus={onFocus}
          onBlur={onBlur}
          onClick={e => {
            unresolved.openUnresolvedFromEvent(e);
          }}
        />
      </Dropdown>
      {editor.isEmpty && placeholder && (
        <div className={styles.placeholder}>{placeholder}</div>
      )}
      {unresolved.unresolvedTooltip}
      {chipPreview.chipPreviewTooltip}
    </div>
  );
};
